import { useState } from 'preact/hooks';
import type { Session } from '../api/client';
import type { WireEntry } from '../api/wire';
import {
  choicesFor,
  editEntry,
  nextDay,
  inverseOf,
  transition,
  type Choice,
  type Conflict,
  type MutationResult,
} from '../api/mutations';
import { entryRow } from '../api/adapter';

import { ActionSheet } from '../components/sheets/ActionSheet.jsx';
import { SheetAction } from '../components/sheets/SheetAction.jsx';
import { Pressable } from '../components/a11y/Pressable';
import { EntryRow } from '../components/entries/EntryRow.jsx';

const GLYPH: Record<string, string> = {
  complete: 'x',
  reopen: '·',
  reopen_question: '?',
  // `glyphStateFor` renders an answered question as `done`, so the row that
  // produces that state carries the same mark. Not `!` — that is priority.
  answer: 'x',
  edit: '✐',
  migrate: '>',
  migrate_month: '>',
  schedule: '□',
  cancel: '~',
  uncancel: '·',
};


/** `SheetAction` renders `<div onClick>` — the same missing semantics as
 *  CaptureBar and TabBar, and here it is on the app's mutation controls, which
 *  is the worst place for it: complete, migrate and cancel were not reachable by
 *  keyboard and unnamed to a screen reader. Wrapped rather than edited, so the
 *  copied component stays verbatim against the design source.
 */
function Action({
  action,
  label,
  hint,
  destructive,
  disabled,
  onPress,
}: {
  action: string;
  label: string;
  hint: string;
  destructive?: boolean;
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      label={hint ? `${label} — ${hint}` : label}
      onPress={disabled ? () => {} : onPress}
      style={disabled ? { cursor: 'default' } : {}}
    >
      <SheetAction
        glyph={GLYPH[action] ?? '\u00b7'}
        label={label}
        hint={hint}
        destructive={destructive}
        disabled={disabled}
      />
    </Pressable>
  );
}

type Step =
  | { at: 'choices' }
  | { at: 'confirm'; choice: Choice }
  | { at: 'prompt'; choice: Choice; value: string }
  | { at: 'edit'; value: string }
  | { at: 'text'; choice: Choice; value: string }
  | { at: 'working' }
  | { at: 'conflict'; conflict: Conflict }
  | { at: 'error'; message: string; code: string };

/** Everything you can do to one entry.
 *
 *  Friction is proportional to regret: completing is one tap because the toast
 *  undoes it, cancelling takes a second worded confirmation, and anything
 *  needing a date asks for one rather than guessing.
 */
export function EntryActions({
  session,
  entry,
  journalDate,
  onDone,
  onDismiss,
}: {
  session: Session;
  entry: WireEntry;
  journalDate: string | null;
  /** `undo` is present only for a transition with a single inverse, so the
   *  toast never offers an UNDO that cannot be performed. */
  onDone: (summary: string, undo?: () => Promise<void>) => void;
  onDismiss: () => void;
}) {
  const [step, setStep] = useState<Step>({ at: 'choices' });
  // The server refuses a migration to a date before the entry's own day with
  // `entry.migration.backwards`. Bound the picker so it cannot be chosen.
  const minDate = entry.active_date ?? journalDate ?? undefined;
  const row = entryRow(entry);
  const choices = choicesFor(entry);

  async function run(choice: Choice, value?: string) {
    setStep({ at: 'working' });
    try {
      let result: MutationResult;
      if (choice.action === 'edit') {
        result = await editEntry(session, entry.id, { content: value ?? '' });
      } else if (choice.action === 'migrate' || choice.action === 'schedule') {
        result = await transition(session, entry.id, choice.action, {
          destination_date: value ?? '',
        });
      } else if (choice.action === 'migrate_month') {
        result = await transition(session, entry.id, 'migrate_month', {
          destination_month: value ?? '',
        });
      } else if (choice.action === 'answer') {
        result = await transition(session, entry.id, 'answer', { answer_text: value ?? '' });
      } else {
        result = await transition(session, entry.id, choice.action);
      }

      if (result.kind === 'conflict') {
        setStep({ at: 'conflict', conflict: result });
        return;
      }
      const back = choice.action === 'edit' ? null : inverseOf(choice.action);
      onDone(
        `${choice.label} · ${entry.content}`,
        back
          ? async () => {
              // Re-reads the ETag, so undoing after the entry moved on is a
              // conflict rather than a silent overwrite.
              await transition(session, entry.id, back);
            }
          : undefined,
      );
    } catch (error) {
      const anyErr = error as { message?: string; code?: string };
      setStep({
        at: 'error',
        message: anyErr.message ?? String(error),
        code: anyErr.code ?? 'unknown',
      });
    }
  }

  function start(choice: Choice) {
    if (choice.confirm) return setStep({ at: 'confirm', choice });
    if (choice.action === 'edit') return setStep({ at: 'edit', value: entry.content });
    if (choice.immediate === 'tomorrow') {
      const base = entry.active_date ?? journalDate;
      if (!base) return setStep({ at: 'error', message: 'No day to migrate from', code: 'no_date' });
      return void run(choice, nextDay(base));
    }
    if (choice.prompts === 'text') return setStep({ at: 'text', choice, value: '' });
    if (choice.prompts === 'date') {
      const base = entry.active_date ?? journalDate ?? '';
      return setStep({ at: 'prompt', choice, value: base ? nextDay(base) : '' });
    }
    if (choice.prompts === 'month') {
      return setStep({ at: 'prompt', choice, value: (entry.active_date ?? journalDate ?? '').slice(0, 7) });
    }
    void run(choice);
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 35,
        // No `overflow: hidden` here: it clipped the native date picker, which
        // opens downward from an input sitting near the bottom of the screen.
      }}
    >
    <ActionSheet
      label={entry.kind.toUpperCase()}
      title={entry.content}
      meta={`revision ${entry.revision}`}
      onDismiss={onDismiss}
      style={{
        maxHeight: '86vh',
        overflowY: 'auto',
        // Clear of the side insets, which the design system's sheet does not
        // know about — on the iPhone Duo one side is the clock's.
        paddingLeft: 'calc(var(--bujo-gutter) + env(safe-area-inset-left))',
        paddingRight: 'calc(var(--bujo-gutter) + env(safe-area-inset-right))',
        // A date or month prompt needs headroom beneath it, or the picker opens
        // off the bottom of the screen. Lifting the sheet is simpler and more
        // reliable than trying to reposition browser-owned UI.
        paddingBottom:
          step.at === 'prompt'
            ? 'calc(38vh + env(safe-area-inset-bottom))'
            : 'env(safe-area-inset-bottom)',
      }}
    >
      <EntryRow
        state={row.state}
        text={row.text}
        priority={row.priority}
        tokens={row.tokens}
        meta={row.meta}
        divider={false}
      />

      {step.at === 'choices' ? (
        choices.length ? (
          choices.map((choice) => (
            <Action
              key={choice.action}
              action={choice.action}
              label={choice.label}
              hint={choice.consequence}
              destructive={choice.confirm}
              onPress={() => start(choice)}
            />
          ))
        ) : (
          <Note>Nothing to do — a migrated entry is a record, not a task.</Note>
        )
      ) : null}

      {step.at === 'working' ? <Note>Working…</Note> : null}

      {step.at === 'confirm' ? (
        <>
          <Note>
            {step.choice.label} this {entry.kind}? It {step.choice.consequence}.
          </Note>
          <Action
            action={step.choice.action}
            label={`Yes, ${step.choice.label.toLowerCase()} it`}
            hint="this is the confirmation"
            destructive
            onPress={() => void run(step.choice)}
          />
          <Action action="reopen" label="Keep it" hint="nothing changes" onPress={onDismiss} />
        </>
      ) : null}

      {step.at === 'prompt' ? (
        // A real form, so the keyboard's Go / Return key submits. Without one
        // the field looked finished and Enter did nothing.
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (step.value) void run(step.choice, step.value);
          }}
          style={{ display: 'contents' }}
        >
          <Note>
            {step.choice.prompts === 'month' ? 'Which month?' : 'Which date?'}
          </Note>
          <input
            // Native pickers rather than a typed string. `date` yields
            // `YYYY-MM-DD` and `month` yields `YYYY-MM`, which are exactly the
            // formats `destination_date` and `destination_month` want — so there
            // is no format to get wrong. The previous `inputMode="numeric"` was
            // worse than useless on iOS: that keypad has no hyphen, so a date
            // could not be typed at all.
            type={step.choice.prompts === 'month' ? 'month' : 'date'}
            value={step.value}
            onInput={(e) =>
              setStep({ ...step, value: (e.target as HTMLInputElement).value })
            }
            min={step.choice.prompts === 'month' ? undefined : minDate}
            enterkeyhint="go"
            style={promptInput}
          />
          <Action
            action={step.choice.action}
            label={step.choice.label}
            hint={step.value || 'pick a value'}
            disabled={!step.value}
            onPress={() => void run(step.choice, step.value)}
          />
        </form>
      ) : null}

      {step.at === 'text' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (step.value.trim()) void run(step.choice, step.value.trim());
          }}
          style={{ display: 'contents' }}
        >
          <Note>{step.choice.consequence}.</Note>
          <input
            value={step.value}
            onInput={(e) =>
              setStep({ at: 'text', choice: step.choice, value: (e.target as HTMLInputElement).value })
            }
            autocapitalize="sentences"
            spellcheck
            enterkeyhint="done"
            style={promptInput}
          />
          <Action
            action={step.choice.action}
            label={step.choice.label}
            hint={step.value.trim() ? 'records it' : 'cannot be empty'}
            disabled={!step.value.trim()}
            onPress={() => void run(step.choice, step.value.trim())}
          />
        </form>
      ) : null}

      {step.at === 'edit' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (step.value.trim()) {
              void run({ action: 'edit', label: 'Edit', consequence: '' }, step.value.trim());
            }
          }}
          style={{ display: 'contents' }}
        >
          <Note>Editing the text keeps the entry and its history.</Note>
          <input
            value={step.value}
            onInput={(e) => setStep({ at: 'edit', value: (e.target as HTMLInputElement).value })}
            autocapitalize="sentences"
            spellcheck
            enterkeyhint="done"
            style={promptInput}
          />
          <Action
            action="edit"
            label="Save"
            hint={step.value.trim() ? 'replaces the text' : 'text cannot be empty'}
            disabled={!step.value.trim()}
            onPress={() =>
              void run({ action: 'edit', label: 'Edit', consequence: '' }, step.value.trim())
            }
          />
        </form>
      ) : null}

      {step.at === 'conflict' ? (
        <ConflictView conflict={step.conflict} entry={entry} onDismiss={onDismiss} />
      ) : null}

      {step.at === 'error' ? (
        <>
          <Note tone="error">
            {step.message}
          </Note>
          <Note>
            code {step.code} · nothing changed · the entry is still as it was.
          </Note>
          <Action action="reopen" label="Back" hint="" onPress={() => setStep({ at: 'choices' })} />
        </>
      ) : null}
    </ActionSheet>
    </div>
  );
}

/** A revision race. Both versions in full; the app refuses to guess. */
function ConflictView({
  conflict,
  entry,
  onDismiss,
}: {
  conflict: Conflict;
  entry: WireEntry;
  onDismiss: () => void;
}) {
  const server = entryRow(conflict.server);
  return (
    <>
      <Note tone="error">CONFLICT · 412 · entry changed elsewhere</Note>
      <Note>
        Your action was made against revision {conflict.mine}; the server is now at{' '}
        {conflict.server.revision}, changed by another client. Nothing was discarded.
      </Note>
      <div style={{ paddingTop: 'var(--bujo-space-2)' }}>
        <SectionNote>ON THE SERVER NOW</SectionNote>
        <EntryRow
          state={server.state}
          text={server.text}
          priority={server.priority}
          tokens={server.tokens}
          meta={server.meta}
          divider={false}
        />
      </div>
      <div>
        <SectionNote>WHAT YOU SAW</SectionNote>
        <EntryRow
          state={entryRow(entry).state}
          text={entry.content}
          priority={entry.priority}
          divider={false}
        />
      </div>
      <Action
        action="reopen"
        label="Keep the server version"
        hint="discards mine"
        onPress={onDismiss}
      />
    </>
  );
}

function Note({ children, tone }: { children: unknown; tone?: 'error' }) {
  return (
    <p
      style={{
        margin: 0,
        padding: 'var(--bujo-space-2) 0',
        font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/var(--bujo-lh-row) var(--bujo-font-mono)',
        color: tone === 'error' ? 'var(--bujo-state-error)' : 'var(--bujo-text-meta)',
      }}
    >
      {children as never}
    </p>
  );
}

function SectionNote({ children }: { children: unknown }) {
  return (
    <span
      style={{
        font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
        letterSpacing: 'var(--bujo-ls-label)',
        color: 'var(--bujo-label)',
      }}
    >
      {children as never}
    </span>
  );
}

const promptInput = {
  width: '100%',
  minHeight: 'var(--bujo-tap-min)',
  background: 'var(--bujo-surface-page)',
  border: '1px solid var(--bujo-rule-hairline)',
  color: 'var(--bujo-text-primary)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-sheet)/1 var(--bujo-font-mono)',
  padding: '0 var(--bujo-space-3)',
  borderRadius: 0,
};
